import { useEffect, useState, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEstimationStore } from '../../stores/estimationStore';
import {
  getProject, updateProject, forkProject, listProjectForks,
  listCollaborators, addCollaborator, updateCollaborator, removeCollaborator,
} from '../../api/projects';
import type { ProjectCollaborator } from '../../api/projects';
import { getOrganization } from '../../api/organizations';
import { listUsers } from '../../api/users';
import type { UserProfile } from '../../api/users';
import { getCantonName } from '../../utils/cantonData';
import { OrgFlag } from '../shared/OrgFlag';
import { toast } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import { hasMinRole } from '../../utils/rbac';
import { useEstimation } from '../../hooks/useEstimation';
import { useFinanceStore } from '../../stores/financeStore';
import { fmtM } from '../../utils/format';
import { EstimationTab } from '../estimation/EstimationTab';
import { VersionHistoryPanel } from '../versioning/VersionHistoryPanel';
import { PLQEntryList } from '../projects/PLQEntryList';
import { ChatPanel } from '../ai/ChatPanel';
import { CommentsPanel } from '../estimation/CommentsPanel';
import type { GrandProjet } from '../../types/estimation';
import type { Organization } from '../../types/organization';

const DemographicsTab = lazy(() => import('../demographics/DemographicsTab').then(m => ({ default: m.DemographicsTab })));
const RatiosTab = lazy(() => import('../ratios/RatiosTab').then(m => ({ default: m.RatiosTab })));
const ParametersTab = lazy(() => import('../estimation/ParametersTab').then(m => ({ default: m.ParametersTab })));
const ResultsN2Tab = lazy(() => import('../estimation/ResultsN2Tab').then(m => ({ default: m.ResultsN2Tab })));
const CommuneTab = lazy(() => import('../estimation/CommuneTab').then(m => ({ default: m.CommuneTab })));
const SubsidiesTab = lazy(() => import('../subsidies/SubsidiesTab').then(m => ({ default: m.SubsidiesTab })));
const AmortizationTab = lazy(() => import('../amortization/AmortizationTab').then(m => ({ default: m.AmortizationTab })));
const PlanningTab = lazy(() => import('../planning/PlanningTab').then(m => ({ default: m.PlanningTab })));
const PopulationTab = lazy(() => import('../population/PopulationTab').then(m => ({ default: m.PopulationTab })));
const ChargesRevenuesTab = lazy(() => import('../finances/ChargesRevenuesTab').then(m => ({ default: m.ChargesRevenuesTab })));
const MunicipalFinanceTab = lazy(() => import('../finances/MunicipalFinanceTab').then(m => ({ default: m.MunicipalFinanceTab })));
const AnalysisReportTab = lazy(() => import('../report/AnalysisReportTab').then(m => ({ default: m.AnalysisReportTab })));
const ExportTab = lazy(() => import('../export/ExportTab').then(m => ({ default: m.ExportTab })));
const ComparisonTab = lazy(() => import('../estimation/ComparisonTab').then(m => ({ default: m.ComparisonTab })));
const ActualsTab = lazy(() => import('../actuals/ActualsPage').then(m => ({ default: m.ActualsPage })));
const SensitivityAnalysisTab = lazy(() => import('../estimation/SensitivityAnalysisTab').then(m => ({ default: m.SensitivityAnalysisTab })));
const ProgrammeDetailleTab = lazy(() => import('../programme/ProgrammeDetailleTab').then(m => ({ default: m.ProgrammeDetailleTab })));

/* ------------------------------------------------------------------
 * Financial Planning Summary — displayed on the project detail page
 * ------------------------------------------------------------------ */
const AMORT_SLICES: Record<string, { weight: number; duration: number }[]> = {
  infra:    [{ weight: 0.60, duration: 40 }, { weight: 0.40, duration: 30 }],
  ep:       [{ weight: 1.0, duration: 20 }],
  ecoles:   [{ weight: 1.0, duration: 33 }],
  creches:  [{ weight: 1.0, duration: 33 }],
  co:       [{ weight: 1.0, duration: 33 }],
  sport:    [{ weight: 1.0, duration: 25 }],
  culturel: [{ weight: 1.0, duration: 33 }],
  zi:       [{ weight: 0.60, duration: 40 }, { weight: 0.40, duration: 30 }],
};

function FinancialPlanningSection({ onTabSwitch }: { onTabSwitch: (tab: string) => void }) {
  const { t } = useTranslation(['finances', 'subsidies', 'amortization']);
  const r = useEstimation();
  const exploitation = useFinanceStore((s) => s.exploitation);
  const useForfait = useFinanceStore((s) => s.useForfait);
  const forfait = useFinanceStore((s) => s.forfait);
  const revenus = useFinanceStore((s) => s.revenus);

  // Subsidies
  const fidu_log = r.logements * 7000;
  const fidu_ep = r.espaces_publics * 50;
  const pa_ge_ep = Math.round((r.cost_ep_mix - fidu_ep) * 0.5);
  const fidu_ec = r.classes * 700000;
  const fi_cr = r.creche_places * 5000;
  const ofas_cr = r.creche_places * 5000 * 2;
  const ofas_para = r.classes * 20 * 0.85 * 3000 * 3;
  const totalSubventions = fidu_log + fidu_ep + pa_ge_ep + fidu_ec + fi_cr + ofas_cr + ofas_para;
  const netCostCommune = r.cost_total - totalSubventions;

  // Amortization
  const costs: Record<string, number> = {
    infra: r.cost_infra_mix, ep: r.cost_ep_mix, ecoles: r.cost_ecoles,
    creches: r.cost_creches, co: r.cost_co, sport: r.cost_sport,
    culturel: r.cost_culturel, zi: r.zi?.cost_total ?? 0,
  };
  const annualAmort = Object.entries(costs).reduce((sum, [key, cost]) => {
    const slices = AMORT_SLICES[key];
    if (!slices || cost <= 0) return sum;
    return sum + slices.reduce((s, sl) => s + (cost * sl.weight) / sl.duration, 0);
  }, 0);

  // Operating charges & revenues
  const totalCharges = useForfait
    ? forfait * r.inhabitants
    : Object.entries(exploitation).reduce((sum, [key, item]) => {
        if (key === 'creches') return sum + item.val * r.creche_places;
        return sum + item.val * r.inhabitants;
      }, 0);

  const fdap = r.creche_places * 8719;
  const totalRevenus = Object.entries(revenus).reduce((sum, [key, item]) => {
    if (key === 'impots_pm' || key === 'impots_nr') return sum + item.val * r.emplois;
    if (key === 'peréquation' || key === 'autres_rev') return sum + item.val;
    return sum + item.val * Math.round(r.inhabitants * 0.5);
  }, 0) + fdap;

  const soldeExploitation = totalRevenus - totalCharges;
  const soldeNet = soldeExploitation - annualAmort;

  if (r.cost_total <= 0) return null;

  const metricStyle: React.CSSProperties = { textAlign: 'center', padding: '8px 4px' };
  const valStyle = (color?: string): React.CSSProperties => ({
    fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700,
    color: color || 'var(--color-text)', lineHeight: 1.2,
  });
  const lblStyle: React.CSSProperties = { fontSize: 9, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 2 };
  const tabBtnStyle: React.CSSProperties = {
    padding: '4px 10px', fontSize: 10, fontWeight: 600,
    border: '1px solid rgba(99,102,241,0.2)', borderRadius: 4,
    background: 'rgba(99,102,241,0.06)', color: 'var(--color-primary)',
    cursor: 'pointer', whiteSpace: 'nowrap',
  };

  return (
    <div style={{
      padding: 14, marginTop: 10,
      background: 'linear-gradient(135deg, rgba(34,211,238,0.06), rgba(167,139,250,0.04))',
      border: '1px solid rgba(34,211,238,0.15)',
      borderRadius: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#22d3ee' }}>
          {t('finances:planificationFinanciere', 'Planification financière')}
        </span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button style={tabBtnStyle} onClick={() => onTabSwitch('subsidies')}>{t('subsidies:tabName', 'Subventions')}</button>
          <button style={tabBtnStyle} onClick={() => onTabSwitch('amortization')}>{t('amortization:tabName', 'Amortissement')}</button>
          <button style={tabBtnStyle} onClick={() => onTabSwitch('finances')}>{t('finances:tabName', 'Charges & Revenus')}</button>
          <button style={tabBtnStyle} onClick={() => onTabSwitch('municipal')}>{t('finances:municipalTabName', 'Finances communales')}</button>
        </div>
      </div>

      {/* Row 1: Investment & Subsidies */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 2,
        padding: 8, marginBottom: 6,
        background: 'rgba(255,255,255,0.02)', borderRadius: 6,
        border: '1px solid rgba(255,255,255,0.04)',
      }}>
        <div style={metricStyle}>
          <div style={valStyle()}>{fmtM(r.cost_total)}</div>
          <div style={lblStyle}>{t('finances:investissementBrut', 'Investissement brut')}</div>
        </div>
        <div style={metricStyle}>
          <div style={valStyle('#6a9a6a')}>{fmtM(totalSubventions)}</div>
          <div style={lblStyle}>{t('finances:subventionsEstimees', 'Subventions estimées')}</div>
        </div>
        <div style={metricStyle}>
          <div style={valStyle('#e87')}>{fmtM(netCostCommune)}</div>
          <div style={lblStyle}>{t('finances:coutNetCommune', 'Coût net commune')}</div>
        </div>
        <div style={metricStyle}>
          <div style={valStyle('#a78bfa')}>{fmtM(annualAmort)}</div>
          <div style={lblStyle}>{t('finances:amortissementPerYear', 'Amortissement / an')}</div>
        </div>
        <div style={metricStyle}>
          <div style={valStyle(soldeNet >= 0 ? '#6db' : '#e87')}>{fmtM(soldeNet)}</div>
          <div style={lblStyle}>{t('finances:soldeNetPerYear', 'Solde net / an')}</div>
        </div>
      </div>

      {/* Row 2: Operating detail */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2,
        padding: 8,
        background: 'rgba(255,255,255,0.02)', borderRadius: 6,
        border: '1px solid rgba(255,255,255,0.04)',
      }}>
        <div style={metricStyle}>
          <div style={valStyle('#e87')}>{fmtM(totalCharges)}</div>
          <div style={lblStyle}>{t('finances:chargesExploitationPerYear', "Charges d'exploitation / an")}</div>
        </div>
        <div style={metricStyle}>
          <div style={valStyle('#6db')}>{fmtM(totalRevenus)}</div>
          <div style={lblStyle}>{t('finances:revenusFiscauxPerYear', 'Revenus fiscaux / an')}</div>
        </div>
        <div style={metricStyle}>
          <div style={valStyle(soldeExploitation >= 0 ? '#6db' : '#e87')}>{fmtM(soldeExploitation)}</div>
          <div style={lblStyle}>{t('finances:soldeExploitationPerYear', "Solde d'exploitation / an")}</div>
        </div>
        <div style={metricStyle}>
          <div style={valStyle('#F2C200')}>{fmtM(fdap)}</div>
          <div style={lblStyle}>{t('finances:fdapCrechesPerYear', 'FDAP crèches / an')}</div>
        </div>
      </div>

      {/* Subsidy breakdown bar */}
      <div style={{ marginTop: 8, padding: '6px 8px', fontSize: 10, color: 'var(--color-text-muted)' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {[
            { label: 'FIDU logements', val: fidu_log, color: '#F2C200' },
            { label: 'FIDU espaces publics', val: fidu_ep, color: '#F2C200' },
            { label: 'PA GE espaces publics', val: pa_ge_ep, color: '#8b5cf6' },
            { label: 'FIDU ecole', val: fidu_ec, color: '#F2C200' },
            { label: 'OFAS parascolaire', val: ofas_para, color: '#6a9a6a' },
            { label: 'FI/ACG creche', val: fi_cr, color: '#F2C200' },
            { label: 'OFAS creche', val: ofas_cr, color: '#6a9a6a' },
          ].filter(s => s.val > 0).map((s) => (
            <span key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 6, height: 6, borderRadius: 1, background: s.color, display: 'inline-block' }} />
              {s.label}: {fmtM(s.val)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

const TAB_COMPONENTS: Record<string, React.ComponentType> = {
  estimation: EstimationTab,
  demographics: DemographicsTab,
  ratios: RatiosTab,
  parameters: ParametersTab,
  results_n2: ResultsN2Tab,
  comparison: ComparisonTab,
  commune: CommuneTab,
  subsidies: SubsidiesTab,
  amortization: AmortizationTab,
  planning: PlanningTab,
  population: PopulationTab,
  finances: ChargesRevenuesTab,
  municipal: MunicipalFinanceTab,
  sensitivity: SensitivityAnalysisTab,
  programme: ProgrammeDetailleTab,
  report: AnalysisReportTab,
  export: ExportTab,
};

export function ProjectPage() {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const activeTab = useEstimationStore((s) => s.activeTab);
  const setActiveTab = useEstimationStore((s) => s.setActiveTab);
  const loadTenantRatios = useEstimationStore((s) => s.loadTenantRatios);
  const ratiosError = useEstimationStore((s) => s.ratiosError);
  const reloadTenantRatios = useEstimationStore((s) => s.reloadTenantRatios);
  const hydrate = useEstimationStore((s) => s.hydrate);
  const setHa = useEstimationStore((s) => s.setHa);
  const setHaZi = useEstimationStore((s) => s.setHaZi);
  const setNiveau = useEstimationStore((s) => s.setNiveau);
  const setProjectRatioOverrides = useEstimationStore((s) => s.setProjectRatioOverrides);

  const [project, setProject] = useState<GrandProjet | null>(null);
  const [forkCount, setForkCount] = useState(0);
  const [orgChain, setOrgChain] = useState<Organization[]>([]);

  // Collaborators state
  const [showCollaborators, setShowCollaborators] = useState(false);
  const [collaborators, setCollaborators] = useState<ProjectCollaborator[]>([]);
  const [availableUsers, setAvailableUsers] = useState<UserProfile[]>([]);
  const [addingCollab, setAddingCollab] = useState(false);
  const [newCollabUserId, setNewCollabUserId] = useState('');
  const [newCollabPermission, setNewCollabPermission] = useState('viewer');

  const currentUser = useAuthStore((s) => s.user);
  const isAdmin = currentUser ? hasMinRole(currentUser.role, 'tenant_admin') : false;

  // Inline HA editing
  const [editingHA, setEditingHA] = useState(false);
  const [haQ, setHaQ] = useState(0);  // total area
  const [haI, setHaI] = useState(0);  // ZI area
  const [savingHA, setSavingHA] = useState(false);

  useEffect(() => {
    loadTenantRatios();
  }, [loadTenantRatios]);

  useEffect(() => {
    if (!projectId) return;
    loadProject(projectId);
    listProjectForks(projectId).then((f) => setForkCount(f.length)).catch(() => {});
  }, [projectId, hydrate, setHa, setHaZi, setNiveau]);

  function loadProject(pid: string) {
    getProject(pid).then((p) => {
      setProject(p);
      setHaQ(p.ha_total ?? 30);
      setHaI(p.ha_industriel ?? 0);
      setHa(p.ha_total ?? 30);
      setHaZi(p.ha_industriel ?? 0);
      setNiveau((p.niveau ?? 1) as 1 | 2);
      setProjectRatioOverrides(p.ratio_overrides ?? {});
      if (p.organization_id) loadOrgChain(p.organization_id);
    }).catch((err) => {
      console.error('Failed to load project:', err);
    });
  }

  async function loadOrgChain(orgId: string) {
    try {
      const chain: Organization[] = [];
      let currentId: string | null = orgId;
      while (currentId && chain.length < 3) {
        const org = await getOrganization(currentId);
        chain.unshift(org);
        currentId = org.parent_id;
      }
      setOrgChain(chain);
    } catch (err) {
      console.warn('Failed to load org chain:', err);
    }
  }

  async function handleSaveHA() {
    if (!project) return;
    setSavingHA(true);
    try {
      await updateProject(project.id, {
        ha_total: haQ,
        ha_industriel: haI,
        change_summary: `Updated surfaces: total=${haQ} ha, ZI=${haI} ha`,
      });
      toast.success('Surfaces mises à jour');
      setEditingHA(false);
      loadProject(project.id);
    } catch (err) {
      console.error('Failed to update HA:', err);
      toast.error('Échec de la mise à jour');
    } finally {
      setSavingHA(false);
    }
  }

  async function handleFork() {
    if (!projectId) return;
    try {
      const forked = await forkProject(projectId);
      toast.success(`Scenario "${forked.name}" created.`);
      navigate(`/app/projects/${forked.id}`);
    } catch {
      toast.error(t('failedToCreate'));
    }
  }

  async function toggleCollaborators() {
    if (!projectId) return;
    if (showCollaborators) {
      setShowCollaborators(false);
      return;
    }
    try {
      const collabs = await listCollaborators(projectId);
      setCollaborators(collabs);
      setShowCollaborators(true);
    } catch (err) {
      console.error('Failed to load collaborators:', err);
      toast.error('Failed to load collaborators');
    }
  }

  async function handleAddCollaborator() {
    if (!projectId || !newCollabUserId) return;
    try {
      const collab = await addCollaborator(projectId, newCollabUserId, newCollabPermission);
      setCollaborators((prev) => [...prev, collab]);
      setAddingCollab(false);
      setNewCollabUserId('');
      setNewCollabPermission('viewer');
      toast.success('Collaborator added');
    } catch (err) {
      console.error('Failed to add collaborator:', err);
      toast.error('Failed to add collaborator');
    }
  }

  async function handleUpdatePermission(userId: string, permission: string) {
    if (!projectId) return;
    try {
      const updated = await updateCollaborator(projectId, userId, permission);
      setCollaborators((prev) => prev.map((c) => (c.user_id === userId ? updated : c)));
      toast.success('Permission updated');
    } catch (err) {
      console.error('Failed to update permission:', err);
      toast.error('Failed to update permission');
    }
  }

  async function handleRemoveCollaborator(userId: string) {
    if (!projectId) return;
    if (!confirm('Remove this collaborator?')) return;
    try {
      await removeCollaborator(projectId, userId);
      setCollaborators((prev) => prev.filter((c) => c.user_id !== userId));
      toast.success('Collaborator removed');
    } catch (err) {
      console.error('Failed to remove collaborator:', err);
      toast.error('Failed to remove collaborator');
    }
  }

  async function openAddCollaboratorForm() {
    setAddingCollab(true);
    if (availableUsers.length === 0) {
      try {
        const users = await listUsers();
        setAvailableUsers(users);
      } catch (err) {
        console.error('Failed to load users:', err);
        toast.error('Failed to load user list');
      }
    }
  }

  function formatDate(iso: string | null | undefined) {
    if (!iso) return '\u2014';
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '\u2014';
      return d.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return '\u2014'; }
  }

  const ratiosWarning = ratiosError ? (
    <div style={{
      padding: '10px 16px', marginBottom: 12,
      background: 'rgba(231, 136, 0, 0.08)',
      border: '1px solid rgba(231, 136, 0, 0.25)',
      borderRadius: 8,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      fontSize: 13, color: '#e88800',
    }}>
      <span>Ratios failed to load — using fallback defaults. {ratiosError}</span>
      <button
        onClick={() => reloadTenantRatios()}
        style={{
          padding: '3px 10px', fontSize: 11,
          border: '1px solid rgba(231,136,0,0.4)', borderRadius: 4,
          background: 'transparent', color: '#e88800',
          cursor: 'pointer', whiteSpace: 'nowrap', marginLeft: 12,
        }}
      >Retry</button>
    </div>
  ) : null;

  const cantonOrg = orgChain.find((o) => o.level === 'canton');
  const communes = project?.communes ?? [];

  const projectHeader = project && projectId ? (
    <div style={{
      marginBottom: 16, padding: 16,
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: 10,
    }}>
      {/* Row 1: Name + badges + actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <span style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700 }}>
          {project.name}
        </span>
        <span style={{
          fontSize: 10, padding: '2px 8px',
          background: 'rgba(255,255,255,0.05)', borderRadius: 4,
          textTransform: 'capitalize',
          // Skip the type chip when it duplicates the Custom badge that follows.
          display: (project.type === 'custom' && project.is_custom) ? 'none' : undefined,
        }}>
          {project.type || 'quartier'}
        </span>
        {project.is_custom ? (
          <span style={{
            fontSize: 10, padding: '2px 8px',
            background: 'rgba(99,102,241,0.1)', color: 'var(--color-primary)',
            borderRadius: 4,
          }}>Custom</span>
        ) : (
          <span style={{
            fontSize: 10, padding: '2px 8px',
            background: 'rgba(34,211,238,0.1)', color: '#22d3ee',
            borderRadius: 4,
          }}>Reference</span>
        )}
        {project.forked_from && (
          <span
            style={{
              fontSize: 10, padding: '2px 8px',
              background: 'rgba(34,211,238,0.15)', color: '#22d3ee',
              borderRadius: 4, cursor: 'pointer',
            }}
            onClick={() => navigate(`/app/projects/${project.forked_from}`)}
          >
            forked from {project.forked_from}
          </span>
        )}
        <div style={{ flex: 1 }} />
        {orgChain.length > 0 ? (
          <div style={{ display: 'flex', gap: 6 }}>
            {orgChain.map((org) => (
              <OrgFlag key={org.id} level={org.level} code={org.code} size="sm" />
            ))}
          </div>
        ) : project.organization_id ? (
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
            Org: {project.organization_id}
          </span>
        ) : null}
        {isAdmin && (
          <button
            onClick={toggleCollaborators}
            style={{
              padding: '4px 12px', fontSize: 11,
              background: showCollaborators ? 'rgba(99,102,241,0.1)' : 'transparent',
              border: '1px solid rgba(99,102,241,0.3)',
              borderRadius: 4, color: 'var(--color-primary)', cursor: 'pointer',
            }}
          >{showCollaborators ? 'Hide Collaborators' : 'Collaborators'}</button>
        )}
        <button
          onClick={handleFork}
          style={{
            padding: '4px 12px', fontSize: 11,
            background: 'transparent', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 4, color: 'var(--color-text)', cursor: 'pointer',
          }}
        >{t('forkScenario')}</button>
        <button
          onClick={() => navigate(`/app/projects/${projectId}/scenarios`)}
          style={{
            padding: '4px 12px', fontSize: 11,
            background: forkCount > 0 ? 'rgba(99,102,241,0.1)' : 'transparent',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 4,
            color: forkCount > 0 ? 'var(--color-primary)' : 'var(--color-text)',
            cursor: 'pointer',
          }}
        >{t('scenarios')}{forkCount > 0 ? ` (${forkCount})` : ''}</button>
      </div>

      {/* Row 2: Project parameters */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
        gap: 2, padding: 10, marginBottom: 10,
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.04)',
        borderRadius: 6,
      }}>
        <div style={{ padding: '4px 8px' }}>
          <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)' }}>ID</div>
          <div style={{ fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all' }}>{project.id}</div>
        </div>
        {cantonOrg && (
          <div style={{ padding: '4px 8px' }}>
            <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)' }}>Canton</div>
            <div style={{ fontSize: 12 }}>{getCantonName(cantonOrg.code, i18n.language)} ({cantonOrg.code})</div>
          </div>
        )}
        {communes.length > 0 && (
          <div style={{ padding: '4px 8px' }}>
            <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)' }}>Communes</div>
            <div style={{ fontSize: 12 }}>{communes.join(', ')}</div>
          </div>
        )}
        <div style={{ padding: '4px 8px' }}>
          <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)' }}>Horizon</div>
          <div style={{ fontSize: 12 }}>{project.horizon_years ?? 30} ans</div>
        </div>
        <div style={{ padding: '4px 8px' }}>
          <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)' }}>Créé le</div>
          <div style={{ fontSize: 12 }}>{formatDate(project.created_at)}</div>
        </div>
        <div style={{ padding: '4px 8px' }}>
          <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)' }}>Statut</div>
          <div style={{ fontSize: 12 }}>{project.status || 'draft'}</div>
        </div>
      </div>

      {/* Row 3: Surface areas — inline editable */}
      <div style={{
        padding: 12,
        background: 'linear-gradient(135deg, rgba(99,102,241,0.04), rgba(99,102,241,0.01))',
        border: '1px solid rgba(99,102,241,0.12)',
        borderRadius: 6,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-primary)' }}>
            Surface du périmètre (ha)
          </span>
          {project.is_custom && !editingHA && (
            <button
              onClick={() => {
                setHaQ(project.ha_total ?? 30);
                setHaI(project.ha_industriel ?? 0);
                setEditingHA(true);
              }}
              style={{
                padding: '3px 10px', fontSize: 10,
                border: '1px solid var(--color-primary)', borderRadius: 4,
                background: 'transparent', color: 'var(--color-primary)',
                cursor: 'pointer',
              }}
            >Modifier</button>
          )}
        </div>

        {editingHA ? (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginBottom: 4 }}>Surface totale</div>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={haQ || ''}
                  onChange={(e) => setHaQ(parseFloat(e.target.value) || 0)}
                  style={{
                    width: '100%', padding: '6px 8px',
                    border: '1px solid rgba(99,102,241,0.3)', borderRadius: 4,
                    background: 'rgba(0,0,0,0.2)', color: 'var(--color-text)',
                    fontSize: 14, fontWeight: 600, textAlign: 'center',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginBottom: 4 }}>Zone industrielle</div>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max={haQ}
                  value={haI || ''}
                  onChange={(e) => setHaI(Math.min(parseFloat(e.target.value) || 0, haQ))}
                  style={{
                    width: '100%', padding: '6px 8px',
                    border: '1px solid rgba(99,102,241,0.3)', borderRadius: 4,
                    background: 'rgba(0,0,0,0.2)', color: 'var(--color-text)',
                    fontSize: 14, fontWeight: 600, textAlign: 'center',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginBottom: 4 }}>{t('common:programmeMixte', 'Programme mixte')}</div>
                <div style={{ padding: '6px 8px', fontSize: 14, fontWeight: 700, color: 'var(--color-primary)', textAlign: 'center' }}>
                  {Math.max(0, haQ - haI).toFixed(1)}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setEditingHA(false)}
                disabled={savingHA}
                style={{
                  padding: '4px 12px', fontSize: 11,
                  border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4,
                  background: 'transparent', color: 'var(--color-text)', cursor: 'pointer',
                }}
              >{t('common:cancel', 'Annuler')}</button>
              <button
                onClick={handleSaveHA}
                disabled={savingHA}
                style={{
                  padding: '4px 14px', fontSize: 11,
                  border: 'none', borderRadius: 4,
                  background: 'var(--color-primary)', color: '#fff',
                  fontWeight: 600, cursor: 'pointer',
                  opacity: savingHA ? 0.5 : 1,
                }}
              >{savingHA ? t('common:saving', 'Enregistrement…') : t('common:save', 'Enregistrer')}</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {[
              { label: t('common:surfaceTotale', 'Surface totale'), val: project.ha_total ?? 0, accent: true },
              { label: t('common:zoneIndustrielle', 'Zone industrielle'), val: project.ha_industriel ?? 0 },
              { label: t('common:programmeMixte', 'Programme mixte'), val: Math.max(0, (project.ha_total ?? 0) - (project.ha_industriel ?? 0)) },
            ].map(({ label, val, accent }) => (
              <div key={label} style={{ textAlign: 'center' }}>
                <div style={{
                  fontFamily: 'var(--font-heading)', fontSize: 20, fontWeight: 700,
                  color: accent ? 'var(--color-primary)' : 'var(--color-text)',
                }}>{typeof val === 'number' ? val : 0}</div>
                <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>{label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Collaborators panel */}
      {showCollaborators && (
        <div style={{
          padding: 16, marginTop: 10,
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: 14, fontWeight: 600 }}>Collaborators</span>
            <button
              onClick={openAddCollaboratorForm}
              style={{
                padding: '3px 10px', fontSize: 11,
                border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4,
                background: 'transparent', color: 'var(--color-text)', cursor: 'pointer',
              }}
            >+ Add</button>
          </div>

          {addingCollab && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
              padding: 10, marginBottom: 10,
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 6,
            }}>
              <select
                value={newCollabUserId}
                onChange={(e) => setNewCollabUserId(e.target.value)}
                style={{
                  padding: '5px 8px', fontSize: 12,
                  border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4,
                  background: 'rgba(0,0,0,0.2)', color: 'var(--color-text)',
                  flex: 1, minWidth: 160,
                }}
              >
                <option value="">Select user...</option>
                {availableUsers
                  .filter((u) => !collaborators.some((c) => c.user_id === u.id))
                  .map((u) => (
                    <option key={u.id} value={u.id}>{u.full_name} ({u.email})</option>
                  ))}
              </select>
              <select
                value={newCollabPermission}
                onChange={(e) => setNewCollabPermission(e.target.value)}
                style={{
                  padding: '5px 8px', fontSize: 12,
                  border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4,
                  background: 'rgba(0,0,0,0.2)', color: 'var(--color-text)',
                }}
              >
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="admin">Admin</option>
              </select>
              <button
                onClick={handleAddCollaborator}
                disabled={!newCollabUserId}
                style={{
                  padding: '4px 12px', fontSize: 11,
                  border: 'none', borderRadius: 4,
                  background: 'var(--color-primary)', color: '#fff',
                  fontWeight: 600, cursor: newCollabUserId ? 'pointer' : 'not-allowed',
                  opacity: newCollabUserId ? 1 : 0.5,
                }}
              >Add</button>
              <button
                onClick={() => setAddingCollab(false)}
                style={{
                  padding: '4px 12px', fontSize: 11,
                  border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4,
                  background: 'transparent', color: 'var(--color-text)', cursor: 'pointer',
                }}
              >Cancel</button>
            </div>
          )}

          {collaborators.length === 0 && !addingCollab ? (
            <div style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>No collaborators yet.</div>
          ) : (
            <div>
              {collaborators.map((c) => (
                <div key={c.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '8px 10px',
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{c.user_name || c.user_id.slice(0, 8)}</div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.user_email || ''}
                    </div>
                  </div>
                  <select
                    value={c.permission}
                    onChange={(e) => handleUpdatePermission(c.user_id, e.target.value)}
                    style={{
                      padding: '4px 8px', fontSize: 11,
                      border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4,
                      background: 'rgba(0,0,0,0.2)', color: 'var(--color-text)',
                    }}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </select>
                  <div style={{ fontSize: 10, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                    {formatDate(c.created_at)}
                    {c.granted_by_name && <span> by {c.granted_by_name}</span>}
                  </div>
                  <button
                    onClick={() => handleRemoveCollaborator(c.user_id)}
                    style={{
                      padding: '2px 6px', fontSize: 11,
                      border: '1px solid var(--color-danger, #e74c3c)', borderRadius: 4,
                      background: 'transparent', color: 'var(--color-danger, #e74c3c)',
                      cursor: 'pointer',
                    }}
                  >&times;</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Financial Planning Summary */}
      <FinancialPlanningSection onTabSwitch={setActiveTab} />
    </div>
  ) : null;

  // Actuals tab — pass projectId prop
  if (activeTab === 'actuals' && projectId) {
    return (
      <div>
        {projectHeader}
        <div style={{ display: 'flex', gap: 24 }}>
          <div style={{ flex: 1 }}>
            <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('loading')}</div>}>
              <ActualsTab projectId={projectId} />
            </Suspense>
          </div>
          <div style={{ width: 340, flexShrink: 0 }}>
            <VersionHistoryPanel projectId={projectId} />
            <CommentsPanel projectId={projectId} />
          </div>
        </div>
      </div>
    );
  }

  // PLQ tab has its own component with project context
  if (activeTab === 'plq') {
    return (
      <div>
        {projectHeader}
        <div style={{ display: 'flex', gap: 24 }}>
          <div style={{ flex: 1 }}>
            <PLQEntryList />
          </div>
          {projectId && (
            <div style={{ width: 340, flexShrink: 0 }}>
              <VersionHistoryPanel projectId={projectId} />
              <CommentsPanel projectId={projectId} />
            </div>
          )}
        </div>
      </div>
    );
  }

  const TabComponent = TAB_COMPONENTS[activeTab];

  if (!TabComponent) {
    return (
      <div>
        {projectHeader}
        <div style={{ display: 'flex', gap: 24 }}>
          <div style={{ flex: 1, padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' }}>
            <h2 style={{ fontFamily: 'var(--font-heading)', marginBottom: 8 }}>
              {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
            </h2>
            <p>{t('notImplemented')}</p>
          </div>
          {projectId && (
            <div style={{ width: 340, flexShrink: 0 }}>
              <VersionHistoryPanel projectId={projectId} />
              <CommentsPanel projectId={projectId} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {ratiosWarning}
      {projectHeader}
      <div style={{ display: 'flex', gap: 24 }}>
        <div style={{ flex: 1 }}>
          <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('loading')}</div>}>
            <TabComponent />
          </Suspense>
        </div>
        {projectId && (
          <div style={{ width: 340, flexShrink: 0 }}>
            <VersionHistoryPanel projectId={projectId} />
            <CommentsPanel projectId={projectId} />
          </div>
        )}
        {projectId && <ChatPanel projectId={projectId} />}
      </div>
    </div>
  );
}
